import { useState } from "react";
import { styles } from "../styles";
import { createQuote } from "../models/models";
import { hasCapability, isMine } from "../utils/capabilities";
import BackButton from "../components/BackButton";

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const money = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD" });

// Customer-facing total of a quote's line items + taxes. Materials/charges
// price as qty × (unit cost × markup%); labor as hours × $/hr.
const quoteTotal = (q) => {
  const c = q.costing || {};
  const withMarkup = (l) => num(l.qty) * num(l.cost) * (1 + num(l.markup) / 100);
  const fuelBase = (f) =>
    f.mode === "flat"
      ? num(f.amount)
      : num(f.costPerMile) * num(f.milesOneWay) * 2 * num(f.trips);
  const fuelTotal = (f) => fuelBase(f) * (1 + num(f.markup) / 100);
  const sub =
    (c.labor || []).reduce((s, l) => s + num(l.hours) * num(l.price), 0) +
    (c.materials || []).reduce((s, m) => s + withMarkup(m), 0) +
    (c.charges || []).reduce((s, x) => s + withMarkup(x), 0) +
    (c.fuel || []).reduce((s, f) => s + fuelTotal(f), 0);
  const tax = (c.taxes || []).reduce(
    (s, t) => s + (t.mode === "flat" ? num(t.amount) : sub * (num(t.rate) / 100)),
    0
  );
  return sub + tax;
};

export default function QuotesPage({
  quotesRaw: rawQuotes = [],
  customers = [],
  actions,
  role,
  capabilities,
  user,
  quoteDefaults,
  onOpenQuote,
  onBack,
}) {
  const canCreateQuotes = hasCapability(role, capabilities, "createQuotes");
  // Techs (and anyone without viewAllQuotes) only see their own quotes.
  const quotesRaw = hasCapability(role, capabilities, "viewAllQuotes")
    ? rawQuotes
    : rawQuotes.filter((q) => isMine(q, user));

  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [locationId, setLocationId] = useState("");
  // Locked at create-time: a maintenance quote and a one-time job quote
  // diverge in the editor (contract fields appear) and on approve (Contract
  // vs Job is created). Switching mid-quote would silently nuke half the
  // doc, so it's a delete-and-recreate if the user picks wrong.
  const [quoteType, setQuoteType] = useState("job");

  const [statusFilter, setStatusFilter] = useState("all");
  const [showApproved, setShowApproved] = useState(false);
  const [search, setSearch] = useState("");
  const [fCustomerId, setFCustomerId] = useState("");
  const [fLocationId, setFLocationId] = useState("");
  const [fEquipmentId, setFEquipmentId] = useState("");

  const selectedCustomer = customers.find((c) => c.id === customerId);
  const locationsForCustomer = selectedCustomer?.locations || [];

  const fCustomer = customers.find((c) => c.id === fCustomerId);
  const fLocations = fCustomer?.locations || [];
  const fEquipment =
    fLocations.find((l) => l.id === fLocationId)?.equipment || [];

  const resetForm = () => {
    setTitle("");
    setCustomerId("");
    setLocationId("");
    setQuoteType("job");
  };

  const handleCreate = async () => {
    if (!title || !customerId) return;
    const base = createQuote(title);
    // Seed terms / validity from the org defaults. createQuote()
    // returns sensible neutral values (""/30), so only override when
    // the admin has actually configured defaults.
    const seededBase = {
      ...base,
      terms: quoteDefaults?.terms || base.terms,
      validDays:
        quoteDefaults?.validDays != null && quoteDefaults?.validDays > 0
          ? quoteDefaults.validDays
          : base.validDays,
    };
    const quote = {
      ...seededBase,
      quoteType,
      customerId,
      locationId,
      createdBy: user?.email || "unknown",
      createdByUid: user?.uid || "",
    };
    await actions.addQuote(quote);
    resetForm();
    onOpenQuote(quote, "edit"); // open the editor on the new quote
    setShowCreate(false);
  };

  const custName = (id) => customers.find((c) => c.id === id)?.name || "—";
  const locName = (cId, lId) =>
    customers.find((c) => c.id === cId)?.locations?.find((l) => l.id === lId)
      ?.name || "—";

  const matchesSearch = (q) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    const hay = [q.quoteNumber, q.title, q.scope, q.terms, custName(q.customerId)]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return hay.includes(term);
  };

  const byDate = (a, b) =>
    new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
  const matched = [...quotesRaw]
    .filter((q) => !fCustomerId || q.customerId === fCustomerId)
    .filter((q) => !fLocationId || q.locationId === fLocationId)
    .filter(
      (q) => !fEquipmentId || (q.equipmentIds || []).includes(fEquipmentId)
    )
    .filter(matchesSearch);
  // Approved quotes are locked records — kept in their own documentation
  // section, separate from the active (draft/sent/declined) list.
  const activeQuotes = matched
    .filter((q) => q.status !== "approved")
    .filter((q) => statusFilter === "all" || q.status === statusFilter)
    .sort(byDate);
  const approvedQuotes = matched
    .filter((q) => q.status === "approved")
    .sort(byDate);

  const canDeleteQuotes = role === "admin" || role === "manager";

  const handleDeleteQuote = (q) => {
    const label = `${q.quoteNumber ? `#${q.quoteNumber} — ` : ""}${
      q.title || "(untitled quote)"
    }`;
    if (
      !window.confirm(
        `Are you sure you want to delete this quote?\n\n${label}\n\nThis cannot be undone.`
      )
    )
      return;
    actions.deleteQuote(q.id);
  };

  const renderQuoteRow = (q) => (
    <div
      key={q.id}
      style={{
        ...styles.card,
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
      }}
    >
      <div style={{ flex: 1 }}>
        <strong
          style={{ cursor: "pointer", color: "#1a5fb4" }}
          onClick={() => onOpenQuote(q, "view")}
        >
          {q.quoteNumber ? `${q.quoteNumber} — ` : ""}
          {q.title || "(untitled quote)"}
        </strong>
        <span
          style={{
            fontSize: "11px",
            marginLeft: "8px",
            padding: "1px 6px",
            background: q.status === "approved" ? "#d6f5dd" : "#e0e0e0",
            borderRadius: "4px",
          }}
        >
          {q.status}
        </span>
        <br />
        <span style={{ fontSize: "12px", color: "#666" }}>
          {custName(q.customerId)}
          {q.locationId ? ` / ${locName(q.customerId, q.locationId)}` : ""}
          {" • "}
          {money(quoteTotal(q))}
        </span>
      </div>
      {canDeleteQuotes && (
        <button
          style={{
            ...styles.button,
            margin: 0,
            background: "#fee",
            color: "#a00",
            flexShrink: 0,
          }}
          onClick={() => handleDeleteQuote(q)}
        >
          Delete
        </button>
      )}
    </div>
  );

  return (
    <>
      <BackButton onClick={onBack} />
      <h2>Quotes</h2>

      {canCreateQuotes && (
        <>
          <button
            style={styles.button}
            onClick={() => setShowCreate((s) => !s)}
          >
            {showCreate ? "Hide New Quote" : "New Quote"}
          </button>
          {showCreate && (
            <div style={styles.card}>
              <div style={{ marginBottom: "6px", fontSize: "13px" }}>
                <strong>Quote type:</strong>{" "}
                <label style={{ marginLeft: "8px" }}>
                  <input
                    type="radio"
                    name="quoteType"
                    value="job"
                    checked={quoteType === "job"}
                    onChange={() => setQuoteType("job")}
                  />{" "}
                  One-time job
                </label>{" "}
                <label style={{ marginLeft: "12px" }}>
                  <input
                    type="radio"
                    name="quoteType"
                    value="maintenance"
                    checked={quoteType === "maintenance"}
                    onChange={() => setQuoteType("maintenance")}
                  />{" "}
                  Maintenance contract
                </label>
                <div style={{ fontSize: "11px", color: "#666", marginTop: "2px" }}>
                  {quoteType === "maintenance"
                    ? "Approval creates a PM contract + spawns the visit schedule. Locked at create — delete + restart if you pick wrong."
                    : "Approval creates a one-shot job on the Jobs board (existing flow)."}
                </div>
              </div>
              <input
                style={styles.input}
                placeholder="Quote title / short description"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <br />
              <label>Customer: </label>
              <select
                value={customerId}
                onChange={(e) => {
                  setCustomerId(e.target.value);
                  setLocationId("");
                }}
              >
                <option value="">Select customer</option>
                {[...customers]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
              {customerId && (
                <>
                  {" "}
                  <label>Location: </label>
                  <select
                    value={locationId}
                    onChange={(e) => setLocationId(e.target.value)}
                  >
                    <option value="">(optional)</option>
                    {locationsForCustomer.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                </>
              )}
              <br />
              <button
                style={styles.button}
                onClick={handleCreate}
                disabled={!title || !customerId}
              >
                Create Quote
              </button>
            </div>
          )}
        </>
      )}

      <div style={styles.card}>
        <input
          style={styles.input}
          placeholder="Search by quote #, title, scope, terms, or customer"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div style={{ marginTop: "6px", fontSize: "13px" }}>
          <label>Status: </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All active</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="declined">Declined</option>
          </select>{" "}
          <label>Customer: </label>
          <select
            value={fCustomerId}
            onChange={(e) => {
              setFCustomerId(e.target.value);
              setFLocationId("");
              setFEquipmentId("");
            }}
          >
            <option value="">All</option>
            {[...customers]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
          {fCustomerId && (
            <>
              {" "}
              <label>Location: </label>
              <select
                value={fLocationId}
                onChange={(e) => {
                  setFLocationId(e.target.value);
                  setFEquipmentId("");
                }}
              >
                <option value="">All</option>
                {fLocations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </>
          )}
          {fLocationId && (
            <>
              {" "}
              <label>Equipment: </label>
              <select
                value={fEquipmentId}
                onChange={(e) => setFEquipmentId(e.target.value)}
              >
                <option value="">All</option>
                {fEquipment.map((eq) => (
                  <option key={eq.id} value={eq.id}>
                    {eq.type}
                    {eq.subType ? ` (${eq.subType})` : ""}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
      </div>

      {activeQuotes.length === 0 ? (
        <p style={{ fontSize: "13px", color: "#666" }}>No active quotes.</p>
      ) : (
        activeQuotes.map(renderQuoteRow)
      )}

      {approvedQuotes.length > 0 && (
        <div style={{ marginTop: "12px" }}>
          <div
            style={{ cursor: "pointer", fontSize: "13px", color: "#1a7f37" }}
            onClick={() => setShowApproved((s) => !s)}
          >
            {showApproved ? "▾" : "▸"}{" "}
            <strong>Approved — documentation ({approvedQuotes.length})</strong>
          </div>
          {showApproved && approvedQuotes.map(renderQuoteRow)}
        </div>
      )}
    </>
  );
}
